import { cn } from '@/lib/utils';
import React, { useState } from 'react';

interface CategoryIconProps {
  src?: string;
  alt: string;
  className?: string;
  fallbackIcon?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const CategoryIcon: React.FC<CategoryIconProps> = ({ 
  src, 
  alt, 
  className,
  fallbackIcon,
  size = 'md'
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',  
    lg: 'w-16 h-16',
    xl: 'w-24 h-24'
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  // Fallback icon SVG
  const DefaultIcon = () => (
    <svg 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      className="w-full h-full text-muted-foreground"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
      <circle cx="8.5" cy="8.5" r="1.5"/>
      <polyline points="21,15 16,10 5,21"/>
    </svg>
  );

  if (!src || imageError) {
    return (
      <div className={cn(
        sizeClasses[size],
        'bg-muted rounded-lg flex items-center justify-center overflow-hidden',
        className
      )}>
        {fallbackIcon || <DefaultIcon />}
      </div>
    );
  }

  return (
    <div className={cn(
      sizeClasses[size],
      'bg-muted rounded-lg overflow-hidden relative',
      className
    )}>
      {/* Loading skeleton */}
      {!imageLoaded && (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}
      
      <img
        src={src}
        alt={alt}
        className={cn(
          'w-full h-full object-cover transition-opacity duration-200',
          imageLoaded ? 'opacity-100' : 'opacity-0'
        )}
        onError={handleImageError}
        onLoad={handleImageLoad}
        loading="lazy"
      />
    </div>
  );
};

export default CategoryIcon;